import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { message, notification } from 'antd';
import { useHistory } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import { useIntercom } from 'react-use-intercom';
import { useMutation, useSubscription, useLazyQuery, useReactiveVar } from '@apollo/client';
import _ from 'lodash';
import moment from 'moment';
import copy from 'copy-to-clipboard';
import { v4 as uuidv4 } from 'uuid';

import useWindowDimensions from 'hooks/useWindowDimensions';
import DownloadingIcon from 'components/Icons/DownloadingIcon';
import Loader from 'components/Loader';
import commonUtils from 'utils/utils';
import helpers, { canUserLock, canUserUnlock } from 'helpers/proposal';
import mainhelpers from 'helpers';

import ProposalList from './components/ProposalList';
import DashboardHeader from './components/DashboardHeader';
import ScrollToTopButton from 'components/ScrollToTopButton';
import ProposalItemLoader from './components/ProposalItemLoader';
import DashboardModals from './DashboardModals';
import PublishedProposalModal from '../Proposal/components/Publish/modals/PublishModals/PublishedProposalModal/PublishedProposalModal';

import {
  GET_ALL_PROPOSALS,
  PROPOSAL_CSV,
  DOWNLOAD_PROPOSAL,
  GET_APPROVED_PROPOSALS,
  GET_SINGLE_PROPOSAL,
} from 'graphql/queries/proposalQueries';
import {
  getProposalTitle,
  findAllProposalStatus,
  getCompletion,
  findDraftObject,
} from 'helpers/proposal';
import {
  PROPOSAL_FILTER_ITEMS,
  PROPOSAL_REPORT_FILTER_ITEMS,
  PROPOSALS_LIMIT,
  PROPOSAL_SUBSCRIPTION_TOPIC,
  PROPOSAL_SORT_DISPLAY,
  // POLLING_INTERVAL,
} from 'constants/index';
import {
  getUserId,
  userVar,
  proposalsVar,
  teamMembersVar,
  proposalsStatsVar,
  scheduleSuccessModalVar,
  hubspotUrlParams,
} from 'graphql/cache';
import { USER_DETAILS } from 'graphql/queries/userQueries';
import {
  UPDATE_PROPOSAL_MUTATION,
  BULK_UPDATE_PROPOSALS_MUTATION,
} from 'graphql/mutations/proposalMutation';
import { FETCH_TEAM_MEMBERS } from 'graphql/queries/usersTeamQueries';
import { FETCH_TAGS } from 'graphql/queries/tagsQueries';
// import { PING_POLLING } from 'graphql/queries/pollingQueries';
import { VALIDATE_PIPEDRIVE_TOKEN_MUTATION } from 'graphql/mutations/integrationMutation';
import { UPDATE_DASHBOARD_PROPOSAL_SUBSCRIPTION } from 'graphql/queries/proposalSubscription';

import { ScheduleSuccessModel } from 'pages/Proposal/components/Publish/modals/PublishModals/PublishedProposalModal/ScheduleSuccessModel';

import './Dashboard.scss';

/**
 * Find filtered items
 * @param {object} filterItems - Filter items
 * @returns {array} Filtered items
 */
const findFilteredItems = ({ filterItems }) => {
  if (!filterItems || filterItems[0].checked === true) {
    return [];
  }

  const checked = filterItems.some((f) => f.checked === true);
  // if none is checked then it should work like [All proposals] is checked
  if (!checked) {
    return [];
  }

  return filterItems;
};

/**
 * Find filtered tags
 * @param {object} tagsFilter - Tags filter
 * @param {string} tagsType - Tags type
 * @returns {array} Filtered tags
 */
const findFilteredTags = ({ tagsFilter, tagsType }) => {
  const tagsFill = [];
  if (tagsFilter?.length) {
    for (const tag of tagsFilter) {
      if (tag.checked && tag._id !== 0) {
        tagsFill.push(tag._id);
      }
    }
  }

  return {
    _ids: tagsFill,
    type: tagsType,
  };
};

/**
 * Headers
 * @returns {array} Headers
 */
const headers = [
  { label: 'Project Name', key: 'name' },
  { label: 'Prospect Name', key: 'clientName' },
  { label: 'Watched', key: 'watched' },
  { label: 'Opened', key: 'opened' },
  { label: 'Status', key: 'status' },
  { label: 'Quote', key: 'quote' },
  { label: 'Prospect IP', key: 'clientIP' },
  { label: 'Tags', key: 'tags' },
];

const NEW_USER_DATE = new Date('2025-03-01T21:01:59.834+00:00');

/**
 * Dashboard
 * @returns {React.ReactNode} Dashboard
 */
const Dashboard = () => {
  const history = useHistory();
  const refToTop = useRef();
  const loaderRef = useRef();

  const { trackEvent } = useIntercom();
  const { width, allowedEditing } = useWindowDimensions();

  const user = useReactiveVar(userVar);
  const proposals = useReactiveVar(proposalsVar || []);
  const teamMembers = useReactiveVar(teamMembersVar);
  const proposalsStats = useReactiveVar(proposalsStatsVar);

  const [selectedProposalId, setProposalId] = useState('');
  const [selectedProposalIds, setSelectedProposalIds] = useState([]);
  const [showSelectionCheckboxes, setShowSelectionCheckboxes] = useState(false);
  const [confirmModal, showConfirmModal] = useState(false);
  const [bulkTagsPopover, setBulkTagsPopover] = useState({ visible: false, mode: 'add' });
  const [permissionErrorModal, showPermissionErrorModal] = useState(false);
  const [permissionErrorModalTitle, setPermissionErrorModalTitle] = useState('');
  const [permissionErrorModalBody, setPermissionErrorModalBody] = useState('');
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [trash, showTrash] = useState(false);
  const [duplicateModal, showDuplicateModal] = useState(false);
  const [shareViaEmailModal, showShareViaEmailModal] = useState(false);
  const [restoreModal, showRestoreModal] = useState(false);
  const [disabledEditingModal, setDisabledEditingModal] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedSorting, setSelectedSorting] = useState(null);
  const [sortingList, setSortingList] = useState(PROPOSAL_SORT_DISPLAY);
  const [tagsData, setTagsData] = useState([]);
  const [tagsFilter, setTagsFilter] = useState([]);
  const [loadingMap, setLoadingMap] = useState({});
  const [lockLoadingMap, setLockLoadingMap] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);
  const scheduleSuccessModal = useReactiveVar(scheduleSuccessModalVar);
  const [shouldFetchProposals, setShouldFetchProposals] = useState(false);

  const [reportItems] = useState([
    {
      id: 'current',
      name: `${moment().subtract(1, 'months').format('MMM YY')} - ${moment().format(
        'MMM D, YYYY'
      )}`,
      key: 'current',
    },
    ...PROPOSAL_REPORT_FILTER_ITEMS,
  ]);
  const [selectedDate, setSelectedDate] = useState([]);
  const [showScrollTop, showHideScrollTop] = useState(false);
  const [showItemLoader, setShowItemLoader] = useState(false);
  const fetchingRef = useRef(false); // Track if fetch is in progress
  const [showAddProposalModal, setShowAddProposalModal] = useState(false);
  const [userInfoModal, setUserInfoModal] = useState(false);
  const [teamList, setTeamList] = useState([]);
  const [filterItems, setFilterItems] = useState(PROPOSAL_FILTER_ITEMS);
  const [generateInvoiceModal, setGenerateInvoiceModal] = useState(false);
  const [popConfirmDetails, setPopConfirmDetails] = useState({ show: false });
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [proposalCSVData, setProposalCSVData] = useState([]);
  const [selectedCSVDuration, setSelectedCSVDuration] = useState('');
  const [showShareProposalModal, setShowShareProposalModal] = useState('');
  const [moveModal, showMoveModal] = useState(false);
  const [filterApplied, setFilterApplied] = useState(false);
  const [showFilters, setShowFilters] = useState(width > 992);
  const [tagsType, setTagsType] = useState('or');
  const [tagWrapperWidth, setTagWrapperWidth] = useState(0);
  const [haltAnimation, setHaltAnimation] = useState(false);
  const [loadingPipedriveDealsDetails, setLoadingPipedriveDealsDetails] = useState(false);

  // ignore the updates from BE
  const [ignoreUpdates, setIgnoreUpdates] = useState({});
  const allProposalCursorRef = useRef(null);

  const [, setApprovedProposalCursor] = useState(null);
  const [isFetching, setIsFetching] = useState(0);
  // const [allApprovedProposals, setAllApprovedProposals] = useState([]);

  const downloadRef = useRef(null);

  const PROPOSALS_LIMIT_PER_PAGE = PROPOSALS_LIMIT * (window.innerHeight >= 1250 ? 2 : 1);

  const [fetchAllProposals, { fetchMore, refetch }] = useLazyQuery(GET_ALL_PROPOSALS, {
    // notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: ({ fetchProposals }) => {
      if (fetchProposals) {
        const { proposalsStats, pagingMetadata, proposals: newProposals } = fetchProposals;

        allProposalCursorRef.current = pagingMetadata;
        proposalsStatsVar(findAllProposalStatus(proposalsStats[0]));
        // Initial load should replace, not merge
        proposalsVar(processPropsals([], newProposals));
        setIsFetching(false);

        if (searchValue || selectedDate?.length || findFilteredItems({ filterItems }).length) {
          setFilterApplied(true);
        } else {
          setFilterApplied(false);
        }
        scrollToTop();
      }
    },
  });

  const [
    getApprovedProposals,
    {
      data: approvedProposalsQueryData,
      loading: approvedProposalsLoading,
      // fetchMore: fetchMoreApprovedProposals,
    },
  ] = useLazyQuery(GET_APPROVED_PROPOSALS, {
    fetchPolicy: 'no-cache',
    onCompleted: ({ fetchApprovedProposals }) => {
      const { pagingMetadata } = fetchApprovedProposals;
      setApprovedProposalCursor(pagingMetadata);
      // setAllApprovedProposals([...allApprovedProposals, ...proposals]);
    },
  });

  useEffect(() => {
    if (!user) {
      return;
    }
    getApprovedProposals({
      variables: {
        limit: 100, // make it 10/20
      },
    });

    if (shouldFetchProposals && isFetching === 0) {
      setIsFetching(true);
      fetchAllProposals({
        variables: {
          limit: PROPOSALS_LIMIT_PER_PAGE,
          search: searchValue,
          teamList: teamList,
          filterTags: findFilteredTags({ tagsFilter, tagsType }),
          selectedDate,
          filterItems: findFilteredItems({ filterItems }),
          sort: selectedSorting,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedSorting]);

  const processPropsals = (oldProposals, newProposals) => {
    if (!newProposals || !Array.isArray(newProposals)) {
      return oldProposals || [];
    }

    const currentTime = Date.now();

    return newProposals.map((proposal) => {
      // check whether to ignore the updates
      if (ignoreUpdates?.[proposal._id] > currentTime) {
        // ignore the new value from BE, preserve the current value from FE
        const oldProposal = oldProposals?.find((p) => p._id === proposal._id);
        if (oldProposal?.tags) {
          proposal.tags = oldProposal.tags;
        }
      }

      return proposal;
    });
  };

  const [refetchUser] = useLazyQuery(USER_DETAILS, {
    variables: { id: getUserId() },
    skip: user || !getUserId(),
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (res) => {
      const { fetchUser } = res;
      userVar({ ...user, ...fetchUser });
    },
  });

  const [fetchTags] = useLazyQuery(FETCH_TAGS, {
    variables: {
      auid: user?.teamId || user?._id || '',
    },
    fetchPolicy: 'network-only',
    onCompleted: ({ fetchTags }) => {
      setTagsData(fetchTags);
    },
  });

  useEffect(() => {
    if (user) {
      fetchTags();
    }
  }, [user, fetchTags]);

  const [validatePipedriveToken, { loading: validatingPipedriveToken }] = useMutation(
    VALIDATE_PIPEDRIVE_TOKEN_MUTATION,
    {
      onCompleted: (data) => {
        if (data?.validatePipedriveToken) {
          const expires = new Date(Date.now() + 1000 * 60 * 60 * 24 * 1); // 1 day
          commonUtils.createCookie(
            'pipedriveSelectedId',
            commonUtils.getQueryStringValue('selectedIds'),
            expires
          );
          commonUtils.createCookie(
            'pipedriveToken',
            commonUtils.getQueryStringValue('token'),
            expires
          );
          commonUtils.createCookie(
            'pipedriveResource',
            commonUtils.getQueryStringValue('resource'),
            expires
          );
          setShowAddProposalModal(true);
        } else {
          message.error('Invalid Pipedrive token');
        }
      },
      onError: (error) => {
        console.error('Error validating Pipedrive token:', error);
        if (error?.message === 'User connected with different account') {
          notification.error({
            message: 'User connected with different account',
            description: 'Please connect your Pipedrive account to continue',
          });
        } else if (error?.message === 'User doesnot have pipedrive account') {
          const pipedriveState = uuidv4();
          sessionStorage.setItem('pipedrive-state', pipedriveState);
          const url = `https://oauth.pipedrive.com/oauth/authorize?client_id=${process.env.REACT_APP_PIPEDRIVE_CLIENT_ID}&redirect_uri=${process.env.REACT_APP_API_URL}/api/pipedrive/oauth&state=${pipedriveState}`;

          notification.info({
            message: 'Almost there!',
            description: (
              <div>
                To create a proposal from Pipedrive, you need to complete the integration first.
                Connect Pipedrive to your account
                <br />
                <a
                  href={url}
                  className="pipedrive-connect-link"
                  target="_blank"
                  rel="noopener noreferrer">
                  Connect Pipedrive
                </a>
              </div>
            ),
          });
        } else {
          notification.error({
            message: 'Error validating Pipedrive token',
            description: 'Please reconnect your Pipedrive account to continue',
          });
        }
        history.push('/dashboard');
      },
    }
  );

  const refetchProposal = useCallback(
    async (
      searchValue,
      teamList,
      selectedDate,
      filterItems,
      tagsFilter,
      shouldUpdate = true,
      selectedSorting
    ) => {
      if (user?.paymentStatus?.canLock) {
        return;
      }
      setIsFetching(true);

      const { data } = await refetch({
        limit: PROPOSALS_LIMIT_PER_PAGE,
        search: searchValue,
        teamList: teamList,
        filterTags: findFilteredTags({ tagsFilter, tagsType }),
        selectedDate,
        filterItems: findFilteredItems({ filterItems }),
        sort: selectedSorting,
      });

      if (shouldUpdate && data?.fetchProposals) {
        const { pagingMetadata, proposals: newProposals, proposalsStats } = data.fetchProposals;

        if (proposalsStats?.length) {
          proposalsStatsVar(findAllProposalStatus(proposalsStats[0]));
        }

        allProposalCursorRef.current = pagingMetadata;
        // Refetch should replace current proposals, not merge
        proposalsVar(processPropsals([], newProposals));

        scrollToTop(); // scroll when new proposal loads
      }

      setIsFetching(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refetch, user, tagsType, tagsFilter, proposals, selectedSorting]
  );

  const [updateProposal] = useMutation(UPDATE_PROPOSAL_MUTATION, {
    onCompleted: ({ updateProposal }) => {
      let updatedProposals = [];
      const prevProposals = proposals.filter((prop) => prop._id !== updateProposal._id);
      const prevProposal = proposals.find((prop) => prop._id === updateProposal._id);
      updateProposal = prevProposal ? { ...prevProposal, ...updateProposal } : updateProposal;
      updatedProposals = [updateProposal, ...prevProposals];
      updatedProposals = updatedProposals.sort((p1, p2) => new Date(p2.date) - new Date(p1.date));

      if (
        popConfirmDetails?.show &&
        (popConfirmDetails?.type === 'decline' ||
          popConfirmDetails?.type === 'approve' ||
          popConfirmDetails?.type === 'state')
      ) {
        refetchProposal(searchValue, teamList, selectedDate, filterItems);

        setPopConfirmDetails((prevState) => ({
          ...prevState,
          show: false,
          type: '',
        }));

        message.success(
          `Proposal ${
            popConfirmDetails?.type === 'decline'
              ? 'declined'
              : popConfirmDetails?.type === 'state'
              ? 'state'
              : 'approved'
          } successfully update`
        );
      }
      // updatedProposals is the complete list with one proposal updated
      proposalsVar(processPropsals([], updatedProposals));
    },
  });

  const [bulkUpdateProposals] = useMutation(BULK_UPDATE_PROPOSALS_MUTATION, {
    onCompleted: ({ bulkUpdateProposals: result }) => {
      setBulkLoading(false);
      setBulkTagsPopover({ visible: false, mode: 'add' });
      clearSelection();

      if (!result?.updated?.length) return;

      const updatedMap = new Map(result.updated.map((p) => [p._id, p]));
      const updatedProposals = (proposals || []).map((p) =>
        updatedMap.has(p._id) ? { ...p, ...updatedMap.get(p._id) } : p
      );

      message.success(`${result?.updated?.length} proposal(s) updated successfully`);

      proposalsVar(
        processPropsals(
          [],
          updatedProposals.sort((a, b) => new Date(b.date) - new Date(a.date))
        )
      );

      setPopConfirmDetails((prev) => ({ ...prev, show: false, type: '' }));
      setShowSelectionCheckboxes(false);
    },
    onError: () => {
      setBulkLoading(false);
      setPopConfirmDetails((prev) => ({ ...prev, show: false, type: '' }));
      clearSelection();
      setShowSelectionCheckboxes(false);
      message.error('Failed to update proposals');
    },
  });

  const handleTeamMembersList = (fetchTeamMembers) => {
    let tempTeamList = [
      {
        id: user._id,
        name: 'By Me',
        checked: false,
      },
    ];
    fetchTeamMembers.forEach((userTeam) => {
      if (userTeam._id !== user._id) {
        tempTeamList.push({
          id: userTeam._id,
          name: userTeam.profile.name,
          checked: false,
        });
      }
    });
    if (user.roles && user.roles.includes('editor')) {
      tempTeamList.push({
        id: 'sharedwithme',
        name: 'Shared with Me',
        checked: false,
      });
    }
    setTeamList(tempTeamList);
  };

  const [loadTeamMembers, { loading: isFetchingTeamMembers }] = useLazyQuery(FETCH_TEAM_MEMBERS, {
    variables: user?.roles?.includes('manager') ? { teamId: user?.teamId } : null,
    skip: !getUserId() || user || !teamMembers.length,
    fetchPolicy: 'cache-and-network',
    onCompleted: ({ fetchTeamMembers }) => {
      handleTeamMembersList(fetchTeamMembers);
      teamMembersVar(fetchTeamMembers);
    },
  });

  useSubscription(UPDATE_DASHBOARD_PROPOSAL_SUBSCRIPTION, {
    variables: { topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${user?.teamId || user?._id}` },
    onSubscriptionData: ({ subscriptionData }) => {
      if (!subscriptionData.data) return proposals;

      let { typename, proposal } = subscriptionData.data.updateProposalSubscription;
      let updatedProposals = [];

      if (
        user._id !== proposal.uid &&
        user.teamId !== proposal.auid &&
        user._id !== proposal.auid &&
        typename !== 'DELETE_PROPOSAL'
      )
        return;

      const prevProposals = proposals.filter((prop) => prop._id !== proposal._id);

      if (prevProposals && proposal) {
        if (typename === 'UPDATE_PROPOSAL' || typename === 'UPDATE_CLIENT_PROPOSAL') {
          const prevProposal = proposals.find((prop) => prop._id === proposal._id);
          proposal = prevProposal ? { ...prevProposal, ...proposal } : proposal;
          updatedProposals = [proposal, ...prevProposals];
        } else if (typename === 'DELETE_PROPOSAL')
          updatedProposals = prevProposals.filter((prop) => prop._id !== proposal._id);
        else if (typename === 'RESTORE_PROPOSAL') {
          updatedProposals = prevProposals.filter((prop) => prop._id !== proposal._id);
          showRestoreModal(false);
          setShowSelectionCheckboxes(false);
        } else updatedProposals = [proposal, ...prevProposals];

        updatedProposals = updatedProposals.sort((p1, p2) => new Date(p2.date) - new Date(p1.date));

        // updatedProposals is the complete updated list
        proposalsVar(processPropsals([], updatedProposals));
      }
    },
  });

  const [getSingleProposal] = useLazyQuery(GET_SINGLE_PROPOSAL);

  /*
   * Remove polling
   *
  const {
    data: pollingResponse,
    // startPolling,
    // stopPolling,
  } = useQuery(PING_POLLING, {
    variables: {
      from: 'Dashboard',
      userId: getUserId() || '',
      teamId: user?.teamId || '',
    },
    pollInterval: POLLING_INTERVAL.dashboard * 1000,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (pollingResponse) {
      let typename,
        proposal = undefined;
      typename = pollingResponse?.pingPolling?.typename;
      proposal = pollingResponse?.pingPolling?.proposal;
      if (
        typename &&
        typename !== 'pong' &&
        lastUpdate !== JSON.stringify(pollingResponse?.pingPolling)
      ) {
        setLastUpdate(JSON.stringify(pollingResponse?.pingPolling));
        let updatedProposals = [];

        if (
          user?._id === proposal?.uid ||
          user?.teamId === proposal?.auid ||
          user?._id === proposal?.auid ||
          (user?.roles?.includes('manager') && user?.teamId === proposal?.uid)
        ) {
          const prevProposals = proposals.filter((prop) => prop._id !== proposal._id);

          if (prevProposals && proposal) {
            if (typename === 'UPDATE_PROPOSAL') {
              const prevProposal = proposals.find((prop) => prop._id === proposal._id);
              proposal = prevProposal ? { ...prevProposal, ...proposal } : proposal;
              updatedProposals = [proposal, ...prevProposals];
            } else if (typename === 'DELETE_PROPOSAL')
              updatedProposals = prevProposals.filter((prop) => prop._id !== proposal._id);
            else if (typename === 'RESTORE_PROPOSAL') {
              updatedProposals = prevProposals.filter((prop) => prop._id !== proposal._id);
            } else updatedProposals = [proposal, ...prevProposals];

            updatedProposals = updatedProposals.sort(
              (p1, p2) => new Date(p2.date) - new Date(p1.date)
            );

            proposalsVar(updatedProposals);
          }
        }
      }
    }
  }, [pollingResponse, proposals, user, lastUpdate, setLastUpdate]);

  */

  /*
   * No manual start and stop polling 
   *
  useEffect(() => {
    startPolling(POLLING_INTERVAL.dashboard * 1000);
    return () => {
      stopPolling();
    };
  }, [stopPolling, startPolling]);
  */

  useEffect(() => {
    const cookies = document.cookie.split(';').map((cookie) => cookie.trim());
    const cookieSavedSorting = cookies.find((cookie) => cookie.startsWith('prosperoSorting='));
    const savedSortingValue = cookieSavedSorting
      ? cookieSavedSorting.split('=')[1]
      : 'created_newest';

    const updatedSortingList = PROPOSAL_SORT_DISPLAY.map((option) => ({
      ...option,
      checked: option.key === savedSortingValue,
    }));

    setSelectedSorting(savedSortingValue);
    setSortingList(updatedSortingList);
    setShouldFetchProposals(true);
  }, []);

  useEffect(() => {
    document.title = 'Prospero - Dashboard';
  });

  useEffect(() => {
    const run = async () => {
      const getProposal = async (pid) => {
        const proposalData = await getSingleProposal({
          variables: { pid },
        });
        return proposalData;
      };

      if (hubspotUrlParams()?.type === 'create') {
        const hubspotData = structuredClone(hubspotUrlParams());

        delete hubspotData.type;
        hubspotUrlParams(hubspotData);
        setShowAddProposalModal(true);
      } else if (hubspotUrlParams()?.type === 'delete') {
        const hubspotData = structuredClone(hubspotUrlParams());

        // ✅ await the lazy query
        const proposalData = await getProposal(hubspotUrlParams().pid);

        delete hubspotData.type;
        delete hubspotData.pid;

        hubspotUrlParams(hubspotData);
        if (proposalData?.data?.fetchProposal?._id) {
          setProposalId(proposalData?.data?.fetchProposal?.pid);
          showConfirmModal(true);
        } else {
          showPermissionErrorModal(true);
        }
      } else if (hubspotUrlParams()?.type === 'duplicate') {
        const hubspotData = structuredClone(hubspotUrlParams());

        // ✅ await the lazy query
        const proposalData = await getProposal(hubspotUrlParams().pid);

        delete hubspotData.type;
        delete hubspotData.pid;

        hubspotUrlParams(hubspotData);
        if (proposalData?.data?.fetchProposal?._id) {
          setProposalId(proposalData?.data?.fetchProposal?.pid);
          setSelectedProposal(proposalData?.data?.fetchProposal);
          showDuplicateModal(true);
        } else {
          setPermissionErrorModalTitle('Cannot Duplicate Proposal.');
          setPermissionErrorModalBody("You don't have permission to duplicate this proposal.");
          showPermissionErrorModal(true);
        }
      }
    };

    run();
  }, [getSingleProposal]);

  // const fetchMoreApprovedProposal = async () => {
  //   await fetchMoreApprovedProposals({
  //     variables: {
  //       limit: 100,
  //       cursorPaging: approvedProposalCursor?.cursor,
  //     },
  //   });
  // };

  /**
   * Fetch more proposal
   * @param {string} type - Type
   * @returns {Promise} Promise
   */
  const fetchMoreProposal = useCallback(
    async (type = '') => {
      // Early returns to prevent multiple requests
      if (fetchingRef.current || isFetching || showItemLoader) {
        return;
      }

      if (type === 'delete') {
        // making api call after delete
        // skip fetching new data if there are enough proposal on page
        if (proposals.length >= PROPOSALS_LIMIT) {
          return;
        }

        return;
      }

      const { cursor, hasNext } = allProposalCursorRef.current || {};
      if (!hasNext) {
        return;
      }

      // Set both state and ref to prevent multiple calls
      fetchingRef.current = true;
      setShowItemLoader(true);

      try {
        const { data } = await fetchMore({
          variables: {
            limit: PROPOSALS_LIMIT_PER_PAGE,
            cursorPaging: cursor,
            search: searchValue,
            selectedDate,
            teamList: teamList,
            filterItems: findFilteredItems({ filterItems }),
          },
        });

        const { pagingMetadata, proposalsStats, proposals: newProposals } = data?.fetchProposals;

        if (!newProposals || newProposals.length === 0) {
          return;
        }

        // Update cursor and stats first
        allProposalCursorRef.current = pagingMetadata;
        proposalsStatsVar(findAllProposalStatus(proposalsStats[0]));

        // Get current proposals and merge with new ones, avoiding duplicates
        const currentProposals = proposalsVar() || [];
        const existingIds = new Set(currentProposals.map((p) => p._id));
        const uniqueNewProposals = newProposals.filter((p) => !existingIds.has(p._id));

        if (uniqueNewProposals.length === 0) {
          return;
        }

        const mergedProposals = [...currentProposals, ...uniqueNewProposals];
        const processedProposals = processPropsals(currentProposals, mergedProposals);

        proposalsVar(processedProposals);
        setShowItemLoader(false);
      } catch (error) {
        console.error('Error fetching more proposals:', error);
      } finally {
        fetchingRef.current = false;
        setShowItemLoader(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fetchMore,
      searchValue,
      selectedDate,
      teamList,
      filterItems,
      isFetching,
      showItemLoader,
      proposals,
    ]
  );

  /**
   * Handle observer
   * @param {object} entries - Entries
   * @returns {void}
   */
  const handleObserver = useCallback(
    (entries) => {
      const [target] = entries;

      if (!target?.isIntersecting || fetchingRef?.current) {
        return;
      }

      fetchMoreProposal();
    },
    [fetchingRef, fetchMoreProposal]
  );

  useEffect(() => {
    const option = { root: null, rootMargin: '20px', threshold: 0 };
    const observer = new IntersectionObserver(handleObserver, option);
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [handleObserver]);

  /**
   * Use effect
   * @returns {void}
   */
  useEffect(() => {
    const dashboardHeader = document.querySelector('.dashboard-header');
    const mainContent = document.querySelector('.dashboard');
    let itemHidden = false;
    let scrollExpiry = 0;

    function onScroll() {
      // prevent multiple calls for header animation
      if (Date.now() < scrollExpiry) {
        return;
      }

      // Handle header visibility
      if (!itemHidden && mainContent.scrollTop > 0) {
        showHideScrollTop(true);
        dashboardHeader?.classList.add('hide-items');
        itemHidden = true;
        scrollExpiry = Date.now() + 300;
      } else if (itemHidden && mainContent.scrollTop === 0) {
        showHideScrollTop(false);
        dashboardHeader?.classList.remove('hide-items');
        itemHidden = false;
      }
    }

    if (mainContent) {
      mainContent.addEventListener('scroll', onScroll);
    }

    return () => {
      if (mainContent) {
        mainContent.removeEventListener('scroll', onScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMoreProposal]);

  const newUser = new Date(user?.createdAt) > NEW_USER_DATE;

  /**
   * Use effect
   * @returns {void}
   */
  useEffect(() => {
    if (
      newUser &&
      user?.profile &&
      (!user.profile.onBoardComplete || commonUtils.getQueryStringValue('new'))
    ) {
      setUserInfoModal(true);
    }
  }, [user, newUser]);

  /**
   * Use effect
   * @returns {void}
   */
  useEffect(() => {
    if (user && !teamMembers.length) {
      loadTeamMembers();
    } else if (teamMembers.length) {
      handleTeamMembersList(teamMembers);
    }

    if (user) {
      window.ga('send', 'event', 'dashboard', 'init', window.location.href);
      trackEvent('dashboard');
    }

    return () => {
      if (proposals?.length) proposalsVar([]);
    };
  }, [user, loadTeamMembers]); //eslint-disable-line

  /**
   * Use effect
   * @returns {void}
   */
  // useEffect(() => {
  //   refetchProposal();
  // }, [filterItems, searchValue, teamList, selectedDate, refetchProposal]);

  /**
   * Download CSV
   * @returns {void}
   */
  const downloadCSV = useCallback(() => {
    setTimeout(() => {
      notification.close('download-report-notification');
      downloadRef.current?.link?.click();
      setProposalCSVData([]);
    });
  }, [setProposalCSVData]);

  useEffect(() => proposalCSVData.length && downloadCSV(), [proposalCSVData, downloadCSV]);

  /**
   * Get proposal CSV
   * @returns {void}
   */
  const [getProposalCSV] = useLazyQuery(PROPOSAL_CSV, {
    fetchPolicy: 'no-cache',
    skip: !selectedCSVDuration,
    onCompleted: ({ fetchProposalCSV }) => {
      if (!fetchProposalCSV.length) {
        notification.close('download-report-notification');
        message.error(
          `No proposals were found within the specified date range for download. Please choose alternative dates.`
        );
      } else {
        setProposalCSVData(fetchProposalCSV);
      }
      setSelectedCSVDuration('');
    },
    onError: (error) => {
      message.error(`Report download failed: ${error.message}`);
      notification.close('download-report-notification');
      setSelectedCSVDuration('');
    },
  });

  /**
   * Use effect
   * @returns {void}
   */
  useEffect(() => {
    // on tagsData chnage update the tags filter
    setTagsFilter((prev) => {
      const newData = tagsData.map((tag) => {
        // check the checked status
        const existingTag = prev.find((t) => t._id === tag._id);

        return {
          ...tag,
          checked: existingTag?.checked ?? false,
        };
      });

      if (newData?.[0]?.name !== 'All Tags') {
        newData.unshift({ name: 'All Tags', _id: 0, checked: true });
      }

      return newData;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsData]);

  /**
   * Use effect
   * @returns {void}
   */
  useEffect(() => {
    if (selectedCSVDuration) {
      getProposalCSV({ variables: { duration: selectedCSVDuration } });
    }
  }, [selectedCSVDuration]); //eslint-disable-line

  /**
   * Use effect
   * @returns {void}
   */
  useEffect(() => {
    if (history?.location?.pathname?.includes('/pipedrive/create-proposal') && user) {
      if (
        commonUtils.getQueryStringValue('selectedIds') &&
        commonUtils.getQueryStringValue('token') &&
        window.location?.href?.includes('/pipedrive/create-proposal')
      ) {
        validatePipedriveToken({
          variables: {
            token: commonUtils.getQueryStringValue('token'),
          },
        });
      }
    }
  }, [history.location.pathname, validatePipedriveToken, user]);

  /**
   * Download PDF
   * @returns {void}
   */
  const [downloadPdf] = useLazyQuery(DOWNLOAD_PROPOSAL, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      window.open(data.downloadProposal, '_self');
      notification.close('download-report-notification');
    },
    onError: (error) => {
      commonUtils.showErrorMessage({
        title: 'Problem Downloading PDF',
        message: `Please try in a few minutes or contact support. Error ${error.message}`,
      });
    },
  });

  /**
   * Scroll to top
   * @returns {void}
   */
  const scrollToTop = () => {
    refToTop.current?.scroll({ behavior: 'smooth', top: 0 });
  };

  /**
   * Find proposal by ID
   * @param {string} proposalId - Proposal ID
   * @returns {object} Proposal
   */
  const findProposalById = useCallback(
    (proposalId) => {
      let proposal = proposals.find((item) => item.pid === proposalId || item._id === proposalId);

      if (!proposal && approvedProposalsQueryData?.fetchApprovedProposals?.proposals) {
        proposal = approvedProposalsQueryData.fetchApprovedProposals.proposals.find(
          (item) => item.pid === proposalId
        );
      }

      return proposal;
    },
    [proposals, approvedProposalsQueryData]
  );

  /**
   * Edit proposal
   * @param {object} proposalId - Proposal ID
   * @returns {void}
   */
  const editProposal = ({ proposalId }) => {
    const proposal = findProposalById(proposalId);

    if (!proposal) {
      message.error('Something went wrong');
      return;
    }
    const { version = 0 } = proposal;

    if (version < 5) {
      message.error(
        'This version of Prospero does not support this old proposal, it can no longer be edited'
      );
      return;
    }

    if (proposal.lockedData && !canUserUnlock(user, proposal.lockedData)) {
      message.error('This proposal is locked and cannot be edited.');
      return;
    }

    if (!allowedEditing) {
      setDisabledEditingModal(true);
      return;
    }

    const { delivcomplete, milestonescomplete } = getCompletion({ proposal });
    const allcomplete = delivcomplete && milestonescomplete;

    if (proposal.state === 'published' || proposal.state === 'approved') {
      history.push(`/d/${proposalId}`);
    } else {
      if (allcomplete) {
        if (version > 1 || proposal.draft) {
          // const propsigneddate = proposal.signature && proposal.signature.date;
          history.push(`/d/${proposalId}`);
        }
      } else {
        history.push(`/w/${proposalId}`);
      }
    }
  };

  /**
   * Update proposal title
   * @param {object} proposalId - Proposal ID
   * @param {string} value - Value
   * @returns {void}
   */
  const updateProposalTitle = ({ proposalId, value }) => {
    const proposal = findProposalById(proposalId);
    if (!proposal) {
      return;
    }

    const oldProposals = [...proposals];
    const index = oldProposals.findIndex((item) => item.pid === proposalId);
    if (index < 0) {
      message.warning('This proposal cannot be updated at the moment');
      return;
    }

    const { _id } = proposal;

    const { draft } = findDraftObject({
      what: {
        'draft.header.title': value,
        'draft.header.rawtitle': null,
      },
      proposal,
    });

    const project = {
      ...proposal.project,
      name: value,
    };

    // update title instantly
    oldProposals[index].project = project;
    proposalsVar(processPropsals([], oldProposals));

    updateProposal({
      variables: {
        topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${
          proposal?.channel || proposal?.auid || proposal?.uid
        }`,
        updateType: 'edit_title',
        proposal: {
          id: _id,
          _id,
          pid: proposalId,
          uid: proposal.uid,
          draft,
          edited: new Date(),
          project,
          editEvent: proposal.editEvent,
        },
      },
    });
  };

  /**
   * Update proposal tags
   * @param {object} proposal - Proposal
   * @param {array} tags - Tags
   * @returns {void}
   */
  const updateProposalTags = (proposal, tags) => {
    updateProposal({
      variables: {
        topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${
          proposal?.channel || proposal?.auid || proposal?.uid
        }`,
        updateType: 'edit_tag',
        proposal: {
          id: proposal._id,
          _id: proposal._id,
          pid: proposal.pid,
          uid: proposal.uid,
          tags: tags,
          edited: new Date(),
        },
      },
    });
  };

  /**
   * Use effect
   * @returns {void}
   */
  useEffect(() => {
    // update title on modal
    if (popConfirmDetails?.show && popConfirmDetails?.title === 'Activity') {
      const { proposalId } = popConfirmDetails;
      const proposal = findProposalById(proposalId);
      if (proposal) {
        openlogsModal(proposal, proposalId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals, findProposalById]);

  /**
   * Update proposal client name
   * @param {object} proposalId - Proposal ID
   * @param {string} value - Value
   * @returns {void}
   */
  const updateProposalClientName = ({ proposalId, value }) => {
    const proposal = findProposalById(proposalId);
    if (!proposal) {
      return;
    }

    const { _id } = proposal;

    const { draft } = findDraftObject({
      what: {
        'draft.signature.rawname': null,
        'draft.signature.name': value,
      },
      proposal,
    });

    if (draft?.header?.rawsubtitle?.blocks?.length > 0) {
      const oldValue = proposal?.draft?.variables?.client?.fullName.value;
      // keep only the first block
      for (let i = 0; i < draft.header.rawsubtitle.blocks.length; i++) {
        if (draft.header.rawsubtitle.blocks[i].text.indexOf(oldValue) > -1) {
          draft.header.rawsubtitle.blocks[i].text = draft.header.rawsubtitle.blocks[i].text.replace(
            oldValue,
            value
          );
          if (draft.header.rawsubtitle.blocks[i].inlineStyleRanges.length > 0) {
            // if entire text is styled, then update the length of new text
            draft.header.rawsubtitle.blocks[i].inlineStyleRanges.map((item) => {
              if (item['offset'] === 0 && item['length'] === oldValue.length) {
                item['length'] = value.length;
              }
              return item;
            });
          }
          break;
        }
      }
    }

    const client = {
      ...proposal.client,
      contact: value,
    };

    updateProposal({
      variables: {
        topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${
          proposal?.channel || proposal?.auid || proposal?.uid
        }`,
        updateType: 'edit',
        proposal: {
          id: _id,
          _id,
          pid: proposalId,
          uid: proposal.uid,
          draft,
          client: client,
          edited: new Date(),
          editEvent: proposal.editEvent,
        },
      },
    });
  };

  const [modal, setModal] = useState(null);
  /**
   * Open logs modal
   * @param {object} proposal - Proposal
   * @param {string} proposalId - Proposal ID
   * @returns {void}
   */
  const openlogsModal = (proposal, proposalId) => {
    setModal({
      type: 'open-logs-modal',
      proposal,
      proposalId,
      user,
      onCancel: () => setModal(null),
    });

    // setPopConfirmDetails((prevState) => ({
    //   ...prevState,
    //   show: true,
    //   proposalId,
    //   title: 'Activity',
    //   className: `open-logs-modal ${activeTab} ${activeTab === 2 ? 'no-footer' : ''}`,
    //   body: <ActivityModal {...{ proposal, user, activeTab, setActiveTab }} />,
    //   type: 'openLogs',
    // }));
  };

  /**
   * Published proposal link
   * @param {string} proposalId - Proposal ID
   * @returns {string} Published proposal link
   */
  const publishedProposalLink = (proposalId = selectedProposalId) => {
    const proposal = findProposalById(proposalId);
    // dont copy link for null, project, you, deliverables, milestones, edit
    if (
      !proposal ||
      [null, 'project', 'you', 'deliverables', 'milestones', 'edit'].includes(proposal.state)
    ) {
      return;
    }

    const hostForLink = `.goprospero.com/${helpers.porposalPdfLinkPath(proposal._id)}`;
    let pdfLink = user ? `https://${user.domain}${hostForLink}` : '';

    if (user && user._id !== proposal.uid) {
      const teamMember =
        proposal.uid === user._id || // if proposal is created by user
        proposal.uid === user.teamId || // if proposal is createrd by team owner
        proposal.auid === user._id; // if proposal is createrd by team member

      // const teamMember = teamList && teamList.find((user) => user._id === proposal.uid);
      if (teamMember) {
        pdfLink = `https://${user?.domain}${hostForLink}`;
      } else {
        pdfLink = '';
      }
    }
    return pdfLink;
  };

  /**
   * On click item
   * @param {object} actionType - Action type
   * @param {string} proposalId - Proposal ID
   * @param {string} value - Value
   * @returns {void}
   */
  const onClickItem = ({ actionType, proposalId, value }) => {
    if (
      user?.paymentStatus?.canLock &&
      actionType !== 'download_pdf' &&
      actionType !== 'copy_link'
    ) {
      return;
    }

    if (actionType === 'delete') {
      const proposal = findProposalById(proposalId);
      if (!proposal) {
        message.error('Something went wrong');
        return;
      }
      if (![proposal.uid, proposal.auid].includes(user._id)) {
        return showPermissionErrorModal(true);
      }
      setProposalId(proposalId);
      setSelectedProposalIds([proposalId]);
      showConfirmModal(true);
    } else if (actionType === 'restore') {
      setProposalId(proposalId);
      setSelectedProposalIds([proposalId]);
      showRestoreModal(true);
    } else if (actionType === 'edit') {
      editProposal({ proposalId });
    } else if (actionType === 'decline') {
      setPopConfirmDetails((prevState) => ({
        ...prevState,
        show: true,
        title: 'Mark Declined?',
        body: 'You can change the status back to what it was before. Scheduled emails (if any) will be paused',
        cancelText: 'CANCEL',
        confirmText: 'YES, DECLINE',
        onConfirm: () => approveOrDeclineProposal('decline', proposalId),
        type: 'decline',
      }));
    } else if (actionType === 'approve') {
      setPopConfirmDetails((prevState) => ({
        ...prevState,
        show: true,
        title: 'Mark Approved?',
        body: "When a proposal is marked as approved, it will be sealed and won't be editable.You may download a PDF copy or duplicate it as a new proposal later or change its status back to what it was before.",
        cancelText: 'CANCEL',
        confirmText: 'YES, APPROVE',
        onConfirm: () => approveOrDeclineProposal('approve', proposalId),
        type: 'approve',
      }));
    } else if (actionType === 'update_title') {
      updateProposalTitle({ proposalId, value });
    } else if (actionType === 'update_client') {
      updateProposalClientName({ proposalId, value });
    } else if (actionType === 'duplicate') {
      setProposalId(proposalId);
      showDuplicateModal(true);
    } else if (actionType === 'save_as_template') {
      const templateStates = ['seen', 'approved', 'published', 'pdf', 'sent', 'scheduled'];
      const proposal = findProposalById(proposalId);
      if (!proposal) {
        message.error('Something went wrong');
        return;
      }

      if (templateStates.includes(proposal.state)) {
        showMoveModal(true);
        setProposalId(proposal._id);
      } else {
        message.error('Template is still in edit mode.');
      }
    } else if (actionType === 'generate_fresh_book_invoice') {
      if (!user?.freshbooks) {
        setPopConfirmDetails((prevState) => ({
          ...prevState,
          show: true,
          title: 'FreshBooks Invoice Integration',
          body: 'You can integrate Prospero with an invoice service to generate invoices from your proposals with one click.',
          cancelText: 'CANCEL',
          confirmText: 'SETUP',
          onConfirm: () => history.push('/integration'),
          type: 'integration',
        }));
      } else {
        setProposalId(proposalId);
        setGenerateInvoiceModal('freshbooks');
      }
    } else if (actionType === 'generate_quick_book_invoice') {
      if (!user?.quickbooks) {
        setPopConfirmDetails((prevState) => ({
          ...prevState,
          show: true,
          title: 'QuickBooks Invoice Integration',
          body: 'You can integrate Prospero with an invoice service to generate invoices from your proposals with one click.',
          cancelText: 'CANCEL',
          confirmText: 'SETUP',
          onConfirm: () => history.push('/integration'),
          type: 'integration',
        }));
      } else {
        setProposalId(proposalId);
        setGenerateInvoiceModal('quickbooks');
      }
    } else if (actionType === 'generate_xero_invoice') {
      if (!(user?.xero || {})?.connected) {
        setPopConfirmDetails((prevState) => ({
          ...prevState,
          show: true,
          title: 'Xero Invoice Integration',
          body: 'You can integrate Prospero with an invoice service to generate invoices from your proposals with one click.',
          cancelText: 'CANCEL',
          confirmText: 'SETUP',
          onConfirm: () => history.push('/integration'),
          type: 'integration',
        }));
      } else {
        setProposalId(proposalId);
        setGenerateInvoiceModal('xero');
      }
    } else if (actionType === 'generate_morning_invoice') {
      if (!Object.keys(user?.morninginvoice || {}).length) {
        setPopConfirmDetails((prevState) => ({
          ...prevState,
          show: true,
          title: 'morning Invoice Integration',
          body: 'You can integrate Prospero with an invoice service to generate invoices from your proposals with one click.',
          cancelText: 'CANCEL',
          confirmText: 'SETUP',
          onConfirm: () => history.push('/integration'),
          type: 'integration',
        }));
      } else {
        setProposalId(proposalId);
        setGenerateInvoiceModal('morninginvoice');
      }
    } else if (actionType === 'change_to_previous_state') {
      setProposalId(proposalId);
      const proposal = findProposalById(proposalId);
      if (!proposal) {
        message.error('Something went wrong');
        return;
      }
      const proposalPrevState = proposal.prevState || 'edit';

      setPopConfirmDetails((prevState) => ({
        ...prevState,
        show: true,
        title: `Change proposal state to ${
          proposalPrevState === 'edit'
            ? 'draft'
            : proposalPrevState === 'seen'
            ? 'opened'
            : proposalPrevState
        }?`,
        body: 'This action cannot be undone',
        cancelText: 'CANCEL',
        confirmText: 'YES, CHANGE',
        onConfirm: () => {
          let doc = {};

          if (proposal.state === 'seen') {
            doc = {
              id: proposal._id,
              _id: proposal._id,
              pid: proposalId,
              uid: proposal.uid,
              state: proposalPrevState,
              prevState: '',
              published: '',
              signature: {
                name: '',
                email: '',
                date: null,
                clientIP: '',
              },
              approvedmanually: '',
              pdflink: '',
              disableSig: false,
              edited: new Date(),
              editEvent: proposal.editEvent,
              emailSettings: null,
              isScheduled: false,
              sharedViaMail: false,
              ...(proposal.prevState === '' ? { signature: null } : {}),
            };
          } else {
            doc = {
              id: proposal._id,
              _id: proposal._id,
              pid: proposalId,
              uid: proposal.uid,
              state: proposalPrevState,
              prevState: '',
              lastSeen: '',
              seenCount: 0,
              seenTime: 0,
              published: '',
              signature: {
                name: '',
                date: null,
                email: '',
                clientIP: '',
              },
              approvedmanually: '',
              pdflink: '',
              disableSig: false,
              edited: new Date(),
              editEvent: proposal.editEvent,
              emailSettings: null,
              isScheduled: false,
              sharedViaMail: false,
              ...(proposal.prevState === '' || proposal.state === 'approved'
                ? { signature: null }
                : {}),
            };
          }

          updateProposal({
            variables: {
              topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${doc?.channel || doc?.auid || doc?.uid}`,
              updateType: 'edit',
              proposal: doc,
            },
          });
        },
        type: 'state',
      }));
    } else if (actionType === 'download_pdf') {
      downloadPdf({
        variables: {
          origin: window.location.origin,
          proposalId,
        },
      });
      downloadNotification(`Downloading PDF...`);
    } else if (actionType === 'open_logs') {
      const proposal = findProposalById(proposalId);
      if (proposal) {
        openlogsModal(proposal, proposalId);
      }
    } else if (actionType === 'share_proposal') {
      const proposal = findProposalById(proposalId);
      if (!proposal) {
        message.error('Something went wrong');
        return;
      }

      setShowShareProposalModal({
        _id: proposal._id,
        name: getProposalTitle({ proposal, user }),
      });
    } else if (actionType === 'copy_link') {
      const pdfLink = publishedProposalLink(proposalId);
      copy(pdfLink);
      message.success('Copied to Clipboard');
    } else if (actionType === 'share_via_email') {
      setProposalId(proposalId);
      showShareViaEmailModal(true);
    } else if (actionType === 'lock_unlock') {
      const proposal = findProposalById(proposalId);
      if (!proposal) {
        message.error('Something went wrong');
        return;
      }

      if (value && !canUserLock(user)) {
        message.error(
          'You do not have permission to lock this proposal. Only admin or manager roles can lock proposals.'
        );
        return;
      }

      if (!value && proposal.lockedData && !canUserUnlock(user, proposal.lockedData)) {
        message.error(
          'You do not have permission to unlock this proposal. Only the user who locked it or account admin can unlock it.'
        );
        return;
      }

      updateProposalLocked(proposal, value);
    }
  };

  /**
   * Update proposal locked
   * @param {object} proposal - Proposal
   * @param {boolean} value - Value
   * @returns {void}
   */
  const updateProposalLocked = (proposal, value) => {
    setLockLoadingMap((prev) => ({ ...prev, [proposal.pid]: true }));

    const proposalUpdate = {
      id: proposal._id,
      _id: proposal._id,
      pid: proposal.pid,
      uid: proposal.uid,
    };

    if (value) {
      proposalUpdate.lockedData = {
        uid: user._id,
        name: user?.profile?.name,
        value: true,
        date: new Date().toISOString(),
      };
    } else {
      proposalUpdate.lockedData = null;
    }

    updateProposal({
      variables: {
        topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${
          proposal?.channel || proposal?.auid || proposal?.uid
        }`,
        updateType: 'lock_unlock',
        proposal: proposalUpdate,
      },
      onCompleted: ({ updateProposal: updatedProposal }) => {
        setLockLoadingMap((prev) => ({ ...prev, [proposal.pid]: false }));

        const lockedDataToSet =
          updatedProposal.lockedData !== undefined
            ? updatedProposal.lockedData
            : proposalUpdate.lockedData;

        let finalProposals = [];
        const prevProposals = proposals.filter((prop) => prop._id !== updatedProposal._id);
        const prevProposal = proposals.find((prop) => prop._id === updatedProposal._id);
        const mergedProposal = prevProposal
          ? { ...prevProposal, ...updatedProposal, lockedData: lockedDataToSet }
          : { ...updatedProposal, lockedData: lockedDataToSet };
        finalProposals = [mergedProposal, ...prevProposals];
        finalProposals = finalProposals.sort((p1, p2) => new Date(p2.date) - new Date(p1.date));

        proposalsVar(processPropsals([], finalProposals));

        if (value) {
          commonUtils.showLockedNotification(
            proposal.pid,
            'Only you or account admin can unlock it.'
          );
        }
      },
      onError: () => {
        setLockLoadingMap((prev) => ({ ...prev, [proposal.pid]: false }));
      },
    });
  };

  // if (data && data.fetchProposals) {
  //   if (proposalsCount !== data.fetchProposals.proposalsCount)
  //     proposalsCountVar(data.fetchProposals.proposalsCount);
  // }

  /*
  REMARK: optimised in ProposalList, no need of _.group, _.flatten, _.compact

  const proposalsByMonth = useMemo(() => {
    console.log('proposals 1429', proposals);
    return _.groupBy(proposals, (proposal) => dayjs(proposal.date).format('MMMM YYYY'));
  }, [proposals]);

  const props = _.flatten(
    _.toArray(
      _.map(proposalsByMonth, (proposalList, monthName) => {
        return _.compact(
          _.map(proposalList, (proposal, proposalIndex) => {
            const { pid: proposalId, state = 'edit' } = proposal;

            if (proposalId && proposalId.length < 36) {
              return null;
            }

            const proposalStatus = PROPOSAL_STATUS[state];
            const proposalTitle = getProposalTitle({ proposal, user });
            const clientName = getClientName({ proposal });
            let { minPrice, maxPrice } = proposal?.quotes || { minPrice: '$0', maxPrice: '$0' };

            // minimum value can be 0
            minPrice = limitToZero(minPrice);
            maxPrice = limitToZero(maxPrice);

            const seenTime = getSeenTimeDetails({ proposal });
            const { seenCount, openLogs } = proposal;

            let opened = seenCount || openLogs?.length;
            opened =
              opened &&
              proposalStatus !== 'DRAFT' &&
              new Date(proposal.date) > new Date(2019, 5, 30)
                ? opened
                : null;

            const proposalUser = teamMembers?.length
              ? proposal?.uid !== user?._id
                ? teamMembers?.find((teamMember) => teamMember._id === proposal.uid)
                : user
              : '';

            let avatar = '';

            if (proposalUser) {
              avatar = (
                <Tooltip title={proposalUser?.profile?.name}>
                  <Avatar src={proposalUser?.profile?.image} size={40}>
                    {getUserShortName({ name: proposalUser?.profile?.name })}
                  </Avatar>
                </Tooltip>
              );
            }

            return (
              <div key={proposalIndex + proposalId}>
                {proposalIndex === 0 && <div className="month-name">{monthName}</div>}
                <ProposalItem
                  proposalIndex={proposalIndex}
                  tagWrapperWidth={tagWrapperWidth}
                  setTagWrapperWidth={setTagWrapperWidth}
                  teamInfo={
                    proposalUser
                      ? {
                          userId: user?._id,
                          userRole: user?.roles && user?.roles[0],
                          uid: proposal.uid,
                          auid: proposal.auid,
                        }
                      : null
                  }
                  pnum={proposal.pnum}
                  trash={trash}
                  tags={proposal.tags}
                  name={proposalTitle}
                  client={clientName}
                  avatar={avatar}
                  watched={
                    seenTime &&
                    proposalStatus !== 'DRAFT' &&
                    new Date(proposal.date) > new Date(2019, 5, 30)
                      ? `${seenTime.minutes}:${seenTime.seconds} min`
                      : ''
                  }
                  opened={opened}
                  emailLogs={proposal.emailLogs}
                  lastSeen={proposal.lastSeen}
                  clientIP={proposal?.signature?.clientIP}
                  isSecured={proposal?.isSecured}
                  quote={
                    minPrice === maxPrice
                      ? `${minPrice || '$0'}`
                      : `${minPrice || '$0'} - ${maxPrice || '$0'}`
                  }
                  status={proposalStatus || 'DRAFT'}
                  prevState={PROPOSAL_STATUS[proposal.prevState]}
                  onClickItem={onClickItem}
                  isScheduled={proposal?.isScheduled}
                  scheduleDate={proposal?.emailSettings?.scheduled?.scheduleTime}
                  proposalId={proposalId}
                  isLocked={user?.paymentStatus?.canLock}
                  variables={proposal?.draft?.variables}
                  isDeletable={
                    user?._id === proposal?.auid || //user is admin
                    proposal.isOwner || //user is owner
                    (user?.roles?.includes('manager') && !proposal.isManager) // user is manager and proposal is of editor
                  }
                  auid={user?.teamId || user?._id}
                  pid={proposal._id}
                  tagsData={tagsData}
                  setTagsData={setTagsData}
                  proposalsVar={proposalsVar}
                  proposalList={proposals}
                  updateProposalTags={updateProposalTags}
                  setIgnoreUpdates={setIgnoreUpdates}
                  tagsFilter={tagsFilter}
                  language={proposal?.language}
                  lastStatusUpdateTime={proposal?.lastStatusUpdateTime}
                  lockedData={proposal?.lockedData}
                  lockLoadingMap={lockLoadingMap}
                />
              </div>
            );
          })
        );
      })
    )
  );
*/

  const onChangeSearch = _.debounce((e) => {
    allProposalCursorRef.current = null;
    setSearchValue(e.target.value);
    refetchProposal(e.target.value, teamList, selectedDate, filterItems, tagsFilter);
  }, 1000);

  /**
   * On change filter
   * @param {object} selectedItem - Selected item
   * @returns {void}
   */
  const onChangeFilter = (selectedItem) => {
    var updatedFilterList = filterItems.map((item) =>
      item.id === selectedItem.id
        ? { ...item, checked: !selectedItem.checked }
        : !selectedItem.checked && selectedItem.id !== 0 && item.id === 0
        ? { ...item, checked: false }
        : selectedItem.id === 0 && !selectedItem.checked && item.id !== 0
        ? { ...item, checked: false }
        : item
    );

    const allUpdatedFilterCheck = updatedFilterList.every((x) => x.checked === false);
    if (allUpdatedFilterCheck === true && selectedItem.id !== 0) {
      updatedFilterList[0].checked = true;
    }
    allProposalCursorRef.current = null;
    setFilterItems(updatedFilterList);
    refetchProposal(searchValue, teamList, selectedDate, updatedFilterList, tagsFilter);
  };

  /**
   * On change tags filter
   * @param {object} selectedTag - Selected tag
   * @returns {void}
   */
  const onChangeTagsFilter = (selectedTag) => {
    var updatedTagsList = tagsFilter.map((item) =>
      item._id === selectedTag._id
        ? { ...item, checked: !selectedTag.checked }
        : !selectedTag.checked && selectedTag._id !== 0 && item._id === 0
        ? { ...item, checked: false }
        : selectedTag._id === 0 && !selectedTag.checked && item._id !== 0
        ? { ...item, checked: false }
        : item
    );

    const allUpdatedFilterCheck = updatedTagsList.every((x) => x.checked === false);
    if (allUpdatedFilterCheck === true && selectedTag._id !== 0) {
      updatedTagsList[0].checked = true;
    }
    allProposalCursorRef.current = null;
    setTagsFilter(updatedTagsList);
    refetchProposal(searchValue, teamList, selectedDate, filterItems, updatedTagsList);
  };

  /**
   * On show trash
   * @returns {void}
   */
  const onShowTrash = () => {
    let updatedFilterList = undefined;
    if (!trash) {
      updatedFilterList = filterItems.map((item) =>
        item.key === 'deleted' ? { ...item, checked: true } : { ...item, checked: false }
      );
    } else {
      updatedFilterList = filterItems.map((item) =>
        item.key === 'All' ? { ...item, checked: true } : { ...item, checked: false }
      );
    }
    showTrash(!trash);
    allProposalCursorRef.current = null;
    // empty the list
    proposalsVar([]);

    setFilterItems(updatedFilterList);
    refetchProposal(searchValue, teamList, selectedDate, updatedFilterList, tagsFilter);
  };

  /**
   * Download notification
   * @param {string} downloadMessage - Download message
   * @returns {void}
   */
  const downloadNotification = (downloadMessage) => {
    notification.open({
      key: 'download-report-notification',
      className: 'download-report-notification',
      duration: 0,
      message: downloadMessage,
      placement: 'bottomRight',
      icon: <DownloadingIcon />,
      closeIcon: <></>,
    });
  };

  /**
   * On download report filter
   * @param {object} selectedItem - Selected item
   * @returns {void}
   */
  const onDownloadReportFilter = (selectedItem) => {
    setSelectedCSVDuration(selectedItem.key);
    downloadNotification(`Your report is on it’s way...`);
  };

  /**
   * On change proposal sorting
   * @param {object} sorting - Sorting
   * @returns {void}
   */
  const onChangeProposalSorting = (sorting) => {
    const updatedSortedList = sortingList.map((item) =>
      item.id === sorting.id ? { ...item, checked: !sorting.checked } : { ...item, checked: false }
    );
    setSortingList(updatedSortedList);
    setSelectedSorting(sorting.key);

    document.cookie = `prosperoSorting=${sorting.key}; path=/;`;
    refetchProposal(
      searchValue,
      teamList,
      selectedDate,
      filterItems,
      tagsFilter,
      true,
      sorting.key
    );
  };

  /**
   * On change date
   * @param {object} date - Date
   * @returns {void}
   */
  const onChangeDate = (date) => {
    if (date && date.length) {
      date[0] = date[0]?.startOf('day');
      date[1] = date[1]?.endOf('day');
    }
    allProposalCursorRef.current = null;
    setSelectedDate(date);
    refetchProposal(searchValue, teamList, date, filterItems, tagsFilter);
  };

  /**
   * On change teams filter
   * @param {object} selectedItem - Selected item
   * @returns {void}
   */
  const onChangeTeamsFilter = (selectedItem) => {
    const updatedFilterList = teamList.map((item) =>
      item.id === selectedItem.id ? { ...item, checked: !selectedItem.checked } : item
    );
    allProposalCursorRef.current = null;
    setTeamList(updatedFilterList);
    refetchProposal(searchValue, updatedFilterList, selectedDate, filterItems, tagsFilter);
  };

  /**
   * Approve or decline proposal
   * @param {string} type - Type
   * @param {string} proposalId - Proposal ID
   * @returns {Promise} Promise
   */
  const approveOrDeclineProposal = async (type, proposalId) => {
    const proposal = findProposalById(proposalId);
    if (!proposal) {
      message.error('Something went wrong');
      return;
    }
    // DECLINED
    // TODO: GA
    if (popConfirmDetails.type === 'decline' || type === 'decline') {
      trackEvent('decline', {
        propid: proposalId,
      });

      updateProposal({
        variables: {
          topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${
            proposal?.channel || proposal?.auid || proposal?.uid
          }`,
          updateType: 'declined',
          proposal: {
            id: proposal._id,
            _id: proposal._id,
            pid: proposal.pid,
            uid: proposal.uid,
            state: 'declined',
            prevState: proposal.state,
            published: '',
            signature: null,
          },
        },
      });
    }
    // APPROVE
    if (popConfirmDetails.type === 'approve' || type === 'approve') {
      // TODO: GA
      trackEvent('approve', {
        propid: proposalId,
      });
      let userIP;

      try {
        await Promise.all([
          new Promise((resolve, reject) =>
            fetch('https://www.cloudflare.com/cdn-cgi/trace')
              .then((res) => res.text())
              .then((result) => {
                result = mainhelpers.transformReadableText(result);
                if (result) {
                  userIP = result.ip;
                  resolve(result);
                }
              })
              .catch((error) => {
                reject(error);
              })
          ),
        ]);
      } catch (error) {
        console.error(error);
      }

      if (proposal.state === 'approved') {
        message.success("Can't decline an approved proposal");
      } else {
        updateProposal({
          variables: {
            topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${
              proposal?.channel || proposal?.auid || proposal?.uid
            }`,
            updateType: 'approved',
            proposal: {
              id: proposal._id,
              _id: proposal._id,
              pid: proposal.pid,
              uid: proposal.uid,
              state: 'approved',
              prevState: proposal.state,
              approvedmanually: new Date(new Date().toUTCString()).toISOString(),
              disableSig: true,
              signature: {
                name: proposal?.name,
                clientIP: userIP,
              },
            },
          },
        });
      }
    }
  };

  /**
   * Toggle proposal selection
   * @param {string} proposalId - Proposal ID
   * @returns {void}
   */
  const toggleProposalSelection = (proposalId) => {
    setSelectedProposalIds((prev) =>
      prev.includes(proposalId) ? prev.filter((id) => id !== proposalId) : [...prev, proposalId]
    );
  };

  /**
   * Clear selection
   * @returns {void}
   */
  const clearSelection = () => setSelectedProposalIds([]);

  /**
   * Check if proposal is selected
   * @param {string} proposalId - Proposal ID
   * @returns
   */
  const isProposalSelected = (proposalId) => selectedProposalIds.includes(proposalId);

  /**
   * Bulk delete
   * @returns {void}
   */
  const onBulkDelete = () => {
    if (selectedProposalIds.length === 0) return;
    setProposalId(selectedProposalIds[0]);
    showConfirmModal(true);
  };

  /**
   * Bulk restore
   * @returns {void}
   */
  const onBulkRestore = () => {
    if (selectedProposalIds.length === 0) return;
    setProposalId(selectedProposalIds[0]);
    showRestoreModal(true);
  };

  /**
   * Bulk approve (single batch request).
   * @returns {void}
   */
  const onBulkApprove = () => {
    if (selectedProposalIds.length === 0) return;

    setPopConfirmDetails((prev) => ({
      ...prev,
      show: true,
      title: 'Mark Approved?',
      body: `You are about to approve ${selectedProposalIds.length} proposal(s). When a proposal is marked as approved, it will be sealed and won't be editable.`,
      cancelText: 'CANCEL',
      confirmText: 'YES, APPROVE',
      onConfirm: async () => {
        setBulkLoading(true);

        let userIP;

        try {
          const res = await fetch('https://www.cloudflare.com/cdn-cgi/trace');
          const text = await res?.text();
          userIP =
            (text && text?.split('\n')?.find((l) => l?.startsWith('ip=')))?.replace('ip=', '') ||
            '';
        } catch {
          userIP = '';
        }

        const topic = `${PROPOSAL_SUBSCRIPTION_TOPIC}_${user?.teamId || user?._id}`;

        const updates = selectedProposalIds
          ?.map((id) => {
            const proposal = findProposalById(id);

            if (
              !proposal ||
              proposal?.state === 'approved' ||
              proposal?.lockedData?.value ||
              proposal?.state === 'declined'
            ) {
              const proposalTitle = commonUtils.changeVariable(
                proposal?.variables,
                getProposalTitle({ proposal, user }),
                user?.profile?.dateFormat
              );

              message.error(
                proposal?.lockedData?.value
                  ? `Proposal is locked and cannot be approved - ${proposalTitle}`
                  : proposal?.state === 'declined'
                  ? `Can't approve a declined proposal - ${proposalTitle}`
                  : proposal?.state === 'approved'
                  ? `Can't approve an approved proposal - ${proposalTitle}`
                  : `Proposal not found ${proposalTitle ? `- ${proposalTitle}` : ''}`
              );
              return null;
            }

            return {
              updateType: 'approved',
              proposal: {
                id: proposal._id,
                _id: proposal._id,
                pid: proposal.pid,
                uid: proposal.uid,
                state: 'approved',
                prevState: proposal.state,
                approvedmanually: new Date(new Date().toUTCString()).toISOString(),
                disableSig: true,
                signature: { name: proposal?.name, clientIP: userIP },
              },
            };
          })
          ?.filter(Boolean);

        if (updates.length) {
          bulkUpdateProposals({ variables: { topic, updates } });
        } else {
          setBulkLoading(false);
          message.info('No proposals to approve');
          setPopConfirmDetails({ show: false });
          setShowSelectionCheckboxes(false);
        }
      },
      type: 'approve',
    }));
  };

  /**
   * Bulk decline (single batch request).
   * @returns {void}
   */
  const onBulkDecline = () => {
    if (selectedProposalIds.length === 0) return;

    setPopConfirmDetails((prev) => ({
      ...prev,
      show: true,
      title: 'Mark Declined?',
      body: `You are about to decline ${selectedProposalIds.length} proposal(s). You can change the status back later.`,
      cancelText: 'CANCEL',
      confirmText: 'YES, DECLINE',
      onConfirm: () => {
        setBulkLoading(true);

        const topic = `${PROPOSAL_SUBSCRIPTION_TOPIC}_${user?.teamId || user?._id}`;

        const updates = selectedProposalIds
          ?.map((id) => {
            const proposal = findProposalById(id);

            if (!proposal) return null;

            if (proposal?.state === 'approved' || proposal?.lockedData?.value) {
              const proposalTitle = commonUtils.changeVariable(
                proposal?.variables,
                getProposalTitle({ proposal, user }),
                user?.profile?.dateFormat
              );

              message.error(
                proposal?.lockedData?.value
                  ? `Proposal is locked and cannot be declined - ${proposalTitle}`
                  : proposal?.state === 'approved'
                  ? `Can't decline an approved proposal - ${proposalTitle}`
                  : `Proposal not found ${proposalTitle ? `- ${proposalTitle}` : ''}`
              );

              return null;
            }

            return {
              updateType: 'declined',
              proposal: {
                id: proposal._id,
                _id: proposal._id,
                pid: proposal.pid,
                uid: proposal.uid,
                state: 'declined',
                prevState: proposal.state,
                published: '',
                signature: null,
              },
            };
          })
          ?.filter(Boolean);

        if (updates.length) {
          bulkUpdateProposals({ variables: { topic, updates } });
        } else {
          setBulkLoading(false);
          message.info('No proposals to decline');
          setPopConfirmDetails({ show: false });
          setShowSelectionCheckboxes(false);
        }
      },
      type: 'decline',
    }));
  };

  /**
   * Bulk add or remove tags
   * @param {string} mode - Mode
   * @returns {void}
   */
  const onBulkAddOrRemoveTags = (mode) => {
    if (selectedProposalIds.length === 0) return;
    const union = new Set();
    selectedProposalIds.forEach((pid) => {
      const p = findProposalById(pid);
      getProposalTagIds(p).forEach((id) => union.add(id));
    });
    setBulkTagsPopover({
      visible: true,
      mode,
      initialSelectedTagIds: Array.from(union),
    });
  };

  /**
   * Get proposal tag ids
   * @param {object} proposal - Proposal
   * @returns
   */
  const getProposalTagIds = (proposal) => {
    const raw = proposal?.tags || [];
    return raw.map((t) => (typeof t === 'object' && t != null && t._id !== undefined ? t._id : t));
  };

  /**
   * Bulk tags confirm (single batch request).
   * @param {array} tagIdsToAdd - Tag IDs to add
   * @param {array} tagIdsToRemove - Tag IDs to remove
   * @returns {void}
   */
  const onBulkTagsConfirm = (tagIdsToAdd, tagIdsToRemove) => {
    const ids = selectedProposalIds;
    const topic = `${PROPOSAL_SUBSCRIPTION_TOPIC}_${user?.teamId || user?._id}`;

    const updates = ids
      ?.map((proposalId) => {
        const proposal = findProposalById(proposalId);

        if (!proposal) return null;

        let newTagIds = [...getProposalTagIds(proposal)];

        if (tagIdsToAdd?.length) {
          const existingSet = new Set(newTagIds);
          tagIdsToAdd.forEach((tagId) => {
            if (!existingSet.has(tagId)) {
              newTagIds.push(tagId);
              existingSet.add(tagId);
            }
          });
        }

        if (tagIdsToRemove?.length) {
          const removeSet = new Set(tagIdsToRemove);
          newTagIds = newTagIds.filter((id) => !removeSet.has(id));
        }

        return {
          updateType: 'edit_tag',
          proposal: {
            id: proposal._id,
            _id: proposal._id,
            pid: proposal.pid,
            uid: proposal.uid,
            tags: newTagIds,
            edited: new Date(),
          },
        };
      })
      ?.filter(Boolean);

    if (updates.length) {
      setBulkLoading(true);
      bulkUpdateProposals({ variables: { topic, updates } });
    } else {
      setBulkLoading(false);
      message.info('No proposals to update tags');
      setBulkTagsPopover({ visible: false, mode: 'add' });
      clearSelection();
    }
  };

  /**
   * Click new proposal
   * @returns {void}
   */
  const onClickNewProposal = () => {
    if (!user?.paymentStatus?.canLock) {
      setShowAddProposalModal(true);
    }
  };

  /**
   * Approved proposals
   * @returns {array} Approved proposals
   */
  const approvedProposals = useMemo(() => {
    if (approvedProposalsQueryData?.fetchApprovedProposals?.proposals) {
      return approvedProposalsQueryData.fetchApprovedProposals.proposals;
    }

    if (approvedProposalsLoading) {
      return [];
    }

    return (proposals || []).filter(
      (p) =>
        p.state === 'approved' &&
        (p.prevState === 'seen' || p.prevState === 'published') &&
        p.signature?.date &&
        mainhelpers.isWithinPastDays(p.signature.date, 3)
    );
  }, [approvedProposalsQueryData, approvedProposalsLoading, proposals]);

  /**
   * Update signed seen state
   * @param {string} propId - Proposal ID
   * @returns {Promise} Promise
   */
  const updateSignedSeenState = async (propId) => {
    setHaltAnimation(true);
    const propIds = Array.isArray(propId) ? propId : [propId];

    const isBulkLoading = propIds.length > 1;
    if (isBulkLoading) {
      setBulkLoading(true);
    }

    const setLoadingState = (id, state) => {
      if (propIds.length === 1) {
        setLoadingMap((prev) => ({ ...prev, [id]: state }));
      }
    };

    const processProposal = async (id) => {
      const proposal = findProposalById(id);
      if (!proposal) return;

      await updateProposal({
        variables: {
          topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${
            proposal?.channel || proposal?.auid || proposal?.uid
          }`,
          updateType: 'draft',
          proposal: {
            id: proposal._id,
            _id: proposal._id,
            pid: proposal.pid,
            uid: proposal.uid,
            prevState: 'draft',
          },
        },
      });
    };

    for (const id of propIds) {
      try {
        setLoadingState(id, true);
        await processProposal(id);
      } finally {
        setLoadingState(id, false);
      }
    }

    if (isBulkLoading) {
      setBulkLoading(false);
    }
  };

  return (
    <div className="container dashboard-container">
      {(validatingPipedriveToken || loadingPipedriveDealsDetails) && (
        <Loader className="dashboard-loader" />
      )}
      <div
        className={`dashboard 
        ${showFilters ? 'filters-active' : 'filters-hidden'}
        ${showScrollTop ? 'scroll-top-active' : 'scroll-top-hidden'}`}
        ref={refToTop}>
        <DashboardHeader
          userPlanType={user?.planType}
          canLockUser={user?.paymentStatus?.canLock}
          approvedCount={proposalsStats?.approvedCount}
          acceptanceRate={proposalsStats?.acceptanceRate}
          onChangeSearch={onChangeSearch}
          onChangeFilter={onChangeFilter}
          onChangeTagsFilter={onChangeTagsFilter}
          trash={trash}
          showTrash={showTrash}
          onShowTrash={onShowTrash}
          onChangeDate={onChangeDate}
          onChangeTeamsFilter={onChangeTeamsFilter}
          onDownloadReportFilter={onDownloadReportFilter}
          filterItems={filterItems}
          reportItems={reportItems}
          selectedDate={selectedDate}
          teamList={teamList}
          isTeamMember={user?.teamId ? true : false}
          reviewWidget={user?.reviewWidget}
          wixReview={!!user?.wix?.length}
          isFetchingTeamMembers={isFetchingTeamMembers}
          onClicknNewProposal={onClickNewProposal}
          showButton={!(!proposals?.length && !filterApplied)}
          tagsData={tagsFilter}
          tagsType={tagsType}
          setTagsType={setTagsType}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          sortingList={sortingList}
          onChangeProposalSorting={onChangeProposalSorting}
          selectedSorting={selectedSorting}
          proposals={proposals}
          isLoading={showItemLoader}
          showEmptyMessage={true}
          tagWrapperWidth={tagWrapperWidth}
          setTagWrapperWidth={setTagWrapperWidth}
          onClickItem={onClickItem}
          auid={user?.teamId || user?._id}
          setTagsData={setTagsData}
          fetchTags={fetchTags}
          proposalsVar={proposalsVar}
          proposalList={proposals}
          updateProposalTags={updateProposalTags}
          setIgnoreUpdates={setIgnoreUpdates}
          tagsFilter={tagsFilter}
          showItemLoader={showItemLoader}
          updateSignedSeenState={updateSignedSeenState}
          loadingMap={loadingMap}
          lockLoadingMap={lockLoadingMap}
          bulkLoading={bulkLoading}
          approvedProposals={approvedProposals}
          approvedProposalsLoading={approvedProposalsLoading}
          haltAnimation={haltAnimation}
          setLoadingMap={setLoadingMap}
          selectedProposalIds={selectedProposalIds}
          showSelectionCheckboxes={showSelectionCheckboxes}
          onToggleSelectionMode={() => {
            setShowSelectionCheckboxes((v) => {
              if (!v) {
                clearSelection();
              }
              return !v;
            });
          }}
          onBulkDelete={onBulkDelete}
          onBulkRestore={onBulkRestore}
          onBulkApprove={onBulkApprove}
          onBulkDecline={onBulkDecline}
          onBulkAddOrRemoveTags={onBulkAddOrRemoveTags}
          onClearSelection={clearSelection}
          bulkTagsPopover={bulkTagsPopover}
          setBulkTagsPopover={setBulkTagsPopover}
          onBulkTagsConfirm={onBulkTagsConfirm}
        />

        {/* //TODO user review */}
        <div
          className={`item-list ${
            !showItemLoader && !isFetching && !proposals?.length ? 'empty-list' : ''
          }`}>
          {!isFetching ? (
            <React.Fragment>
              <ProposalList
                proposals={proposals}
                trash={trash}
                loading={showItemLoader}
                onClickNewProposal={onClickNewProposal}
                filterApplied={filterApplied}
                isLocked={user?.paymentStatus?.canLock}
                itemRendererProps={{
                  user,
                  teamMembers,
                  proposals,
                  tagWrapperWidth,
                  setTagWrapperWidth,
                  onClickItem,
                  tagsData,
                  setTagsData,
                  proposalsVar,
                  updateProposalTags,
                  setIgnoreUpdates,
                  tagsFilter,
                  lockLoadingMap,
                  loadingMap,
                  handleSignedSeen: updateSignedSeenState,
                  showSelectionCheckboxes,
                  selectedProposalIds,
                  isProposalSelected,
                  toggleProposalSelection,
                }}
              />
              {showItemLoader && (
                <div className="loader-container">
                  {Array.from({ length: PROPOSALS_LIMIT - 1 }, (_, x) => (
                    <ProposalItemLoader key={x} />
                  ))}
                </div>
              )}

              {allProposalCursorRef?.current?.hasNext && <div ref={loaderRef}></div>}
            </React.Fragment>
          ) : (
            <div className="loader-container">
              {Array.from({ length: PROPOSALS_LIMIT }, (_, x) => (
                <ProposalItemLoader key={x} />
              ))}
            </div>
          )}
        </div>

        <ScrollToTopButton showScrollTop={showScrollTop} scrollToTop={scrollToTop} />

        {user && (
          <DashboardModals
            user={user}
            refetchUser={refetchUser}
            trash={trash}
            proposals={proposals || []}
            teamMembers={teamMembers}
            selectedProposalId={selectedProposalId}
            selectedProposalIds={selectedProposalIds}
            setSelectedProposalIds={setSelectedProposalIds}
            popConfirmDetails={popConfirmDetails}
            setPopConfirmDetails={setPopConfirmDetails}
            showAddProposalModal={showAddProposalModal}
            setShowAddProposalModal={setShowAddProposalModal}
            confirmModal={confirmModal}
            showConfirmModal={showConfirmModal}
            showAddUserModal={showAddUserModal}
            setShowAddUserModal={setShowAddUserModal}
            duplicateModal={duplicateModal}
            showDuplicateModal={showDuplicateModal}
            setUserInfoModal={setUserInfoModal}
            permissionErrorModal={permissionErrorModal}
            showPermissionErrorModal={showPermissionErrorModal}
            permissionErrorModalTitle={permissionErrorModalTitle}
            permissionErrorModalBody={permissionErrorModalBody}
            selectedProposal={selectedProposal}
            restoreModal={restoreModal}
            showRestoreModal={showRestoreModal}
            generateInvoiceModal={generateInvoiceModal}
            setGenerateInvoiceModal={setGenerateInvoiceModal}
            userInfoModal={userInfoModal}
            showShareProposalModal={showShareProposalModal}
            setShowShareProposalModal={setShowShareProposalModal}
            refetchProposal={refetchProposal}
            moveModal={moveModal}
            showMoveModal={showMoveModal}
            fetchMoreProposal={fetchMoreProposal}
            disabledEditingModal={disabledEditingModal}
            setDisabledEditingModal={setDisabledEditingModal}
            setLoadingPipedriveDealsDetails={setLoadingPipedriveDealsDetails}
            findProposalById={findProposalById}
            modal={modal}
          />
        )}
        {shareViaEmailModal && (
          <PublishedProposalModal
            copyPropsalLink={() => {
              const pdfLink = publishedProposalLink();
              copy(pdfLink);
              message.success('Copied to Clipboard');
            }}
            publishedProposalLink={publishedProposalLink}
            onCancel={() => showShareViaEmailModal(false)}
            configText={{}}
            downloadPdf={downloadPdf}
            proposal={findProposalById(selectedProposalId)}
            onSettingUpdate={(updatedProps) => {
              const updatedProposal = proposals.map((item) =>
                item.pid === selectedProposalId ? { ...item, ...updatedProps } : item
              );
              // updatedProposal is the complete list with one proposal updated
              proposalsVar(processPropsals([], updatedProposal));
            }}
            fromDashboard={true}
          />
        )}
        {scheduleSuccessModal && (
          <ScheduleSuccessModel
            isOpen={true}
            onCancel={() => scheduleSuccessModalVar(false)}
            scheduleInfo={scheduleSuccessModal}
          />
        )}
        <CSVLink
          ref={downloadRef}
          data={proposalCSVData}
          headers={headers}
          filename={'prospero-proposals.csv'}
        />
      </div>
    </div>
  );
};

export default Dashboard;
